/**
 * PublicCompanyHub — Iter-322 flagship redesign
 *
 * Redesign notes:
 *  • Large branded hero banner (banner image OR gradient in brand accent)
 *  • Overlapping avatar/logo + brand name + verified-employer badge inline
 *  • Brand description tagline
 *  • Real, uninflated stats strip (active opps · markets · events this month · hires · since)
 *  • Follow + Share buttons in the hero action row
 *  • "Featured opportunities" — top 3 job cards rendered LARGE with brand accents
 *  • "All openings" — the rest, in a tighter list
 *  • Social links footer (Instagram, LinkedIn, TikTok, Twitter, YouTube, website)
 *
 * Every value comes from real database columns / real aggregate queries.
 * Nothing on this page is fabricated.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Building2, MapPin, Calendar as CalIcon, DollarSign, AlertCircle,
  Briefcase, ShieldCheck, Users, Award, Zap, Share2, Heart, ExternalLink,
  Instagram, Linkedin, Twitter, Youtube, Globe,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import ShareJobButton from '../components/ShareJobButton';

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return null;
  try {
    const [y, m, d] = iso.split('-');
    return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return iso; }
}

function SocialIcon({ href, kind }) {
  if (!href) return null;
  const Icon = {
    instagram: Instagram, linkedin: Linkedin, twitter: Twitter,
    x: Twitter, youtube: Youtube, tiktok: Zap, website: Globe,
  }[kind] || Globe;
  return (
    <a href={href} target="_blank" rel="noreferrer noopener"
       className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 flex items-center justify-center text-white transition-colors"
       data-testid={`hub-social-${kind}`}>
      <Icon className="w-4 h-4" />
    </a>
  );
}

function StatTile({ label, value, sub }) {
  return (
    <div className="px-5 py-4">
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-cyan-100/80 font-bold">{label}</div>
      <div className="mt-1 text-2xl sm:text-3xl font-bold text-white leading-none tracking-tight">{value}</div>
      {sub && <div className="mt-1 text-[11px] text-white/60">{sub}</div>}
    </div>
  );
}

function OpportunityCard({ j, onApply, brandAccent, featured = false }) {
  return (
    <div className={`relative bg-white rounded-2xl border ${featured ? 'border-cyan-200' : 'border-slate-200'} p-5 sm:p-6 hover:shadow-xl hover:border-cyan-300 transition-all hover:-translate-y-0.5 group`}
         data-testid={`company-hub-job-${j.id}`}>
      {featured && (
        <div className="absolute -top-2 left-5 inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[9px] font-bold uppercase tracking-widest shadow">
          <Award className="w-2.5 h-2.5" /> Featured
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-3 mb-2">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-50 to-teal-50 flex items-center justify-center text-cyan-700 shrink-0 ring-1 ring-cyan-100">
              <Briefcase className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-base sm:text-lg font-bold text-slate-900 leading-snug">{j.title}</h3>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {j.is_multi_day && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-800 text-[10px] font-semibold uppercase tracking-wider">
                    Multi-day · {j.shift_count} shifts
                  </span>
                )}
                {j.role_name && (
                  <span className="text-[11px] font-mono uppercase tracking-wider text-slate-500">{j.role_name}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-slate-600 ml-14">
            {j.location_display && (
              <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {j.location_display}</span>
            )}
            {(j.date_range_display || j.date) && (
              <span className="inline-flex items-center gap-1"><CalIcon className="w-3 h-3" /> {j.date_range_display || formatDate(j.date)}{j.start_time && !j.is_multi_day ? ` · ${j.start_time}` : ''}</span>
            )}
            {j.pay_display && (
              <span className="inline-flex items-center gap-1 font-bold text-emerald-700"><DollarSign className="w-3 h-3" /> {j.pay_display}</span>
            )}
          </div>
          {j.description && (
            <p className="text-xs text-slate-500 mt-2.5 line-clamp-2 ml-14">{j.description}</p>
          )}
        </div>
        <div className="flex gap-2 shrink-0 ml-14 sm:ml-0">
          <Button size="sm" onClick={onApply}
                  className="bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 text-white text-xs font-bold shadow shadow-cyan-600/20"
                  data-testid={`company-hub-apply-${j.id}`}>
            View & Apply
          </Button>
          <ShareJobButton variant="icon" testIdSuffix={`hub-${j.id}`} job={{
            id: j.id, title: j.title, location: j.location_display,
            dateRange: j.date_range_display || j.date, payRate: j.pay_display,
            roleName: j.role_name, description: j.description,
          }} />
        </div>
      </div>
    </div>
  );
}

export default function PublicCompanyHub() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [following, setFollowing] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/public/company/${slug}`);
        if (!cancel) setHub(res.data);
      } catch (e) {
        if (!cancel) setError(e.response?.data?.detail || 'Company not found');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [slug]);

  // Personalized social preview (OG) for the clean /c/:slug hub. Mutates the
  // existing meta tags in place so the production crawler-renderer serializes a
  // per-company og:image/title for Facebook/LinkedIn/WhatsApp. Uses a PATH-
  // versioned card URL so Cloudflare (whose cache key ignores query strings)
  // never serves a stale card. Branded index.html defaults are restored on unmount.
  useEffect(() => {
    const company = hub && hub.company;
    if (!company || !slug) return;
    const origin = window.location.origin;
    const name = company.name || 'this company';
    const title = `${name} · Hiring on iEvntür`;
    const desc = (company.brand_description || company.brand_personality
      || `See open roles and work with ${name} on iEvntür.`).toString().slice(0, 200).trim();
    const cardVer = (() => {
      const raw = company.updated_at || company.updatedAt || company.modified_at || '';
      const stamp = String(raw).replace(/[^0-9]/g, '').slice(0, 14);
      return `c3${stamp ? '-' + stamp : ''}`;
    })();
    const img = `${origin}/api/og/v/${cardVer}/company/${slug}.png`;
    const url = `${origin}/c/${slug}`;

    const prevTitle = document.title;
    const restore = [];
    const setMeta = (selector, value) => {
      const el = document.head.querySelector(selector);
      if (el) {
        restore.push([el, el.getAttribute('content')]);
        el.setAttribute('content', value);
      }
    };
    document.title = title;
    setMeta('meta[name="description"]', desc);
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', desc);
    setMeta('meta[property="og:url"]', url);
    setMeta('meta[property="og:image"]', img);
    setMeta('meta[property="og:image:secure_url"]', img);
    setMeta('meta[property="og:image:alt"]', title);
    setMeta('meta[name="twitter:title"]', title);
    setMeta('meta[name="twitter:description"]', desc);
    setMeta('meta[name="twitter:image"]', img);
    return () => {
      document.title = prevTitle;
      restore.forEach(([el, val]) => { if (val != null) el.setAttribute('content', val); });
    };
  }, [hub, slug]);


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500 text-sm">Loading…</div>
      </div>
    );
  }
  if (error || !hub) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-slate-50">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-md border border-slate-200 p-8 text-center" data-testid="public-company-error">
          <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Company not found</h1>
          <p className="text-sm text-slate-600 mb-6">{error || 'This recruiting page is unavailable.'}</p>
          <Button onClick={() => navigate('/login')} data-testid="public-company-error-login">Browse iEvntür</Button>
        </div>
      </div>
    );
  }

  const company = hub.company || {};
  const stats = hub.stats || {};
  const jobs = hub.jobs || [];
  const featured = jobs.slice(0, 3);
  const rest = jobs.slice(3);
  const bannerImage = company.banner_kind === 'image' && company.banner_value ? company.banner_value : null;

  // Social links are stored as JSON with kind→url.
  let socials = [];
  try {
    const s = typeof company.social_links === 'string' ? JSON.parse(company.social_links) : (company.social_links || {});
    socials = Object.entries(s).filter(([, v]) => !!v);
  } catch { /* noop */ }
  if (company.website) socials.push(['website', company.website]);

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="company-hub-root">
      {/* ─────────────── HERO BANNER ─────────────── */}
      <section className="relative overflow-hidden">
        {/* Banner background */}
        <div className="relative h-64 sm:h-80 md:h-[420px] w-full">
          {bannerImage ? (
            <>
              <img src={bannerImage} alt="" className="absolute inset-0 w-full h-full object-cover"
                   style={{ objectPosition: `50% ${company.banner_offset_y || 50}%` }} />
              <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/50 to-slate-900/80" />
            </>
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-600 via-cyan-700 to-teal-800">
              <div className="absolute inset-0 opacity-30" style={{
                backgroundImage: 'radial-gradient(80% 60% at 30% 20%, rgba(255,255,255,0.4) 0%, transparent 60%), radial-gradient(60% 50% at 80% 80%, rgba(20,184,166,0.6) 0%, transparent 60%)',
              }} />
              <div className="absolute inset-0" style={{
                backgroundImage: 'linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)',
                backgroundSize: '48px 48px',
              }} />
            </div>
          )}
        </div>

        {/* Hero content overlaid */}
        <div className="relative -mt-40 sm:-mt-44 md:-mt-56 max-w-6xl mx-auto px-4 sm:px-6 pb-6">
          <div className="flex flex-col md:flex-row md:items-end gap-6">
            {company.logo_url ? (
              <img src={company.logo_url} alt={company.name}
                   className="w-28 h-28 md:w-32 md:h-32 rounded-3xl bg-white p-3 object-contain ring-4 ring-white shadow-2xl"
                   data-testid="company-hub-logo" />
            ) : (
              <div className="w-28 h-28 md:w-32 md:h-32 rounded-3xl bg-white flex items-center justify-center ring-4 ring-white shadow-2xl">
                <Building2 className="w-14 h-14 text-cyan-600" />
              </div>
            )}
            <div className="flex-1 min-w-0 pb-1">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-cyan-200 drop-shadow-sm">
                Hiring on iEvntür
              </div>
              <h1 className="mt-1.5 text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-[1.05] tracking-tight drop-shadow-md"
                  data-testid="company-hub-name">
                {company.name}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500 text-white text-[11px] font-bold uppercase tracking-wider shadow"
                      data-testid="company-hub-verified">
                  <ShieldCheck className="w-3.5 h-3.5" /> Verified Business
                </span>
                {company.state && (
                  <span className="inline-flex items-center gap-1.5 text-sm text-white/90">
                    <MapPin className="w-3.5 h-3.5" /> {company.state}
                  </span>
                )}
                {stats.member_since_year && (
                  <span className="text-[11px] font-mono uppercase tracking-wider text-white/70">
                    Member since {stats.member_since_year}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2 pb-1">
              <Button variant="outline" size="sm" onClick={() => setFollowing(!following)}
                      className={`text-xs font-bold border-white/30 backdrop-blur ${following ? 'bg-white text-slate-900' : 'bg-white/10 text-white hover:bg-white/20'}`}
                      data-testid="company-hub-follow">
                <Heart className={`w-3.5 h-3.5 mr-1.5 ${following ? 'fill-current' : ''}`} />
                {following ? 'Following' : 'Follow'}
              </Button>
              <Button variant="outline" size="sm"
                      className="text-xs font-bold border-white/30 bg-white/10 text-white hover:bg-white/20 backdrop-blur"
                      onClick={() => navigator.share ? navigator.share({ url: window.location.href, title: company.name }) : navigator.clipboard.writeText(window.location.href)}
                      data-testid="company-hub-share">
                <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share
              </Button>
            </div>
          </div>

          {/* Brand description */}
          {company.brand_description && (
            <p className="mt-6 text-base sm:text-lg text-white/95 leading-relaxed max-w-3xl drop-shadow-sm"
               data-testid="company-hub-description">
              {company.brand_description}
            </p>
          )}
        </div>

        {/* Stats strip */}
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-r from-slate-900 via-cyan-900 to-slate-900 rounded-2xl shadow-2xl overflow-hidden ring-1 ring-white/10"
               data-testid="company-hub-stats">
            <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-white/10">
              <StatTile label="Active opps" value={stats.active_opportunities ?? 0} sub="apply directly" />
              <StatTile label="Markets" value={stats.markets ?? 0} sub={stats.markets === 1 ? 'city' : 'cities hiring'} />
              <StatTile label="This month" value={stats.events_this_month ?? 0} sub="events scheduled" />
              <StatTile label="Hires" value={stats.hires_lifetime ?? 0} sub="on iEvntür" />
              <StatTile label="Response" value="< 24h" sub="typical" />
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── FEATURED OPPORTUNITIES ─────────────── */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-12">
        {jobs.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
            <Briefcase className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <div className="text-sm text-slate-600">No active openings right now — check back soon.</div>
          </div>
        ) : (
          <>
            <div className="flex items-baseline justify-between mb-5">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] font-bold text-cyan-700">Featured Opportunities</div>
                <h2 className="mt-1 text-xl sm:text-2xl font-bold text-slate-900">Now hiring</h2>
              </div>
              <div className="text-xs text-slate-500">{jobs.length} open</div>
            </div>
            <div className="space-y-4" data-testid="company-hub-featured">
              {featured.map((j) => (
                <OpportunityCard key={j.id} j={j} featured onApply={() => navigate(`/jobs/${j.id}`)} />
              ))}
            </div>

            {rest.length > 0 && (
              <>
                <div className="mt-10 mb-5">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] font-bold text-slate-500">All Openings</div>
                </div>
                <div className="space-y-3" data-testid="company-hub-jobs">
                  {rest.map((j) => (
                    <OpportunityCard key={j.id} j={j} onApply={() => navigate(`/jobs/${j.id}`)} />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </section>

      {/* ─────────────── FOOTER — social + branding ─────────────── */}
      {(socials.length > 0 || company.brand_personality) && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-16">
          <div className="rounded-2xl bg-slate-900 text-white p-8 sm:p-10">
            <div className="flex flex-col md:flex-row md:items-center gap-6 justify-between">
              <div className="max-w-xl">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] font-bold text-cyan-300">Connect with {company.name}</div>
                <h3 className="mt-2 text-xl font-bold">Follow along across channels.</h3>
                {company.brand_personality && (
                  <p className="mt-2 text-sm text-white/70">{company.brand_personality}</p>
                )}
              </div>
              {socials.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {socials.map(([kind, url]) => (
                    <SocialIcon key={kind} kind={kind} href={url} />
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="mt-6 text-center text-[11px] font-mono uppercase tracking-widest text-slate-400">
            Powered by iEvntür ·
            <a href="/" className="text-cyan-700 hover:underline ml-2 inline-flex items-center gap-1">
              Create your own hub <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </section>
      )}
    </div>
  );
}
